import React, { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSectionFormHelpers,
  STATE_OPTIONS,
  DO_APPLICATION_TYPE,
  BankingApplicationDocument,
} from 'client-experience-toolbox';
import { sendDOFirebaseEvent } from 'utils/utils';
import { useApplication } from 'hooks/useApplication';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Select } from 'drift-ui';
import ContinueButton from 'components/ContinueButton';
import cx from 'classnames';
import styles from './BusinessHistory.scss';
import baseStyles from './BaseFormStyles.scss';

const BusinessHistory: FC = () => {
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const applicationData = application.applicationData as BankingApplicationDocument['applicationData'];
  const [formedOutsideUS, setFormedOutsideUS] = useState<boolean>(false);
  const { patchApplication, patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.BANKING);
  const { selectDropdownHandlerGenerator, clearValidation, regexValidatorGenerator, presenceValidatorGenerator } =
    useSectionFormHelpers({
      errorSetter: setErrors,
      patchApplication,
      updateApplication,
      application,
      fireBaseLogEvent: sendDOFirebaseEvent,
    });
  const { t } = useTranslation();
  const [isPatching, setIsPatching] = useState(false);
  const YEAR_OPTIONS = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1894; year--) {
      years.push({
        label: String(year),
        value: String(year),
      });
    }
    return years;
  }, []);

  const fieldValidators: { [key: string]: () => boolean } = {
    stateFormed: regexValidatorGenerator({
      key: 'applicationData.company.stateFormed',
      regex: /.+/,
      errorText: t('Required'),
    }),
    yearFormed: presenceValidatorGenerator({
      key: 'applicationData.company.yearFormed',
      errorText: t('Required'),
    }),
  };

  const onContinue = async () => {
    if (formedOutsideUS) {
      patchApplication('lockedBy', 'businessHistory');
      updateApplication('lockedBy', 'businessHistory');
      return;
    }
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return;
    }
    await patchFullApplication(setIsPatching);
  };

  const handleCheckboxPress = () => {
    if (!formedOutsideUS) {
      patchApplication('applicationData.company.stateFormed', null);
      updateApplication('applicationData.company.stateFormed', null);
    }
    setFormedOutsideUS((prev) => !prev);
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.businessHistoryFormContainer)}>
      <div className={styles.container}>
        <p className={styles.text}>{t('digital_onboarding.banking.business_history.state_formed_question')}</p>
        <div className={styles.dropdownContainer}>
          <FormControl fullWidth error={!!errors['applicationData.company.stateFormed']}>
            <InputLabel id="label-state-formed">
              {t('digital_onboarding.banking.business_history.label_state_formed')}
            </InputLabel>
            <Select
              slotProps={{
                input: {
                  'data-nid-target': 'business-history-state-formed',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
              labelId="label-state-formed"
              label={t('digital_onboarding.banking.business_history.label_state_formed')}
              value={applicationData?.company?.stateFormed ?? ''}
              onChange={(e) => {
                selectDropdownHandlerGenerator('applicationData.company.stateFormed', clearValidation)(e);
                setFormedOutsideUS(false);
              }}
            >
              {STATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.stateFormed`] && (
              <FormHelperText>{errors[`applicationData.company.stateFormed`]}</FormHelperText>
            )}
          </FormControl>
        </div>
        <label className={styles.checkboxContainer}>
          <Checkbox
            slotProps={{
              input: {
                'data-nid-target': 'business-history-formed-outside-us',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
            checked={formedOutsideUS}
            onChange={handleCheckboxPress}
          />
          <p className={styles.text}>{t('digital_onboarding.banking.business_history.formed_in_usa_checkbox')}</p>
        </label>
        <p className={cx(styles.text, styles.businessFormedMargin)}>
          {t('digital_onboarding.banking.business_history.year_formed_question')}
        </p>
        <div className={styles.dropdownContainer}>
          <FormControl fullWidth error={!!errors['applicationData.company.yearFormed']}>
            <InputLabel id="label-year-formed">
              {t('digital_onboarding.banking.business_history.label_year_formed')}
            </InputLabel>
            <Select
              slotProps={{
                input: {
                  'data-nid-target': 'business-history-year-formed',
                } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
              labelId="label-year-formed"
              label={t('digital_onboarding.banking.business_history.label_year_formed')}
              value={applicationData?.company?.yearFormed ?? ''}
              onChange={selectDropdownHandlerGenerator('applicationData.company.yearFormed', clearValidation)}
            >
              {YEAR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.yearFormed`] && (
              <FormHelperText>{errors[`applicationData.company.yearFormed`]}</FormHelperText>
            )}
          </FormControl>
        </div>
        <div className={styles.buttonContainer}>
          <ContinueButton
            buttonStyles={styles.nextButton}
            title={t('digital_onboarding.next')}
            handleContinueClick={onContinue}
            isPatching={isPatching}
          />
        </div>
      </div>
    </div>
  );
};

export default BusinessHistory;
